import React, { useState, useEffect, useMemo } from 'react';
import { 
  ScanLine, ShoppingCart, Trash2, Plus, Minus, CreditCard, 
  Receipt, X, Check, ArrowLeft, Store, Camera, Search,
  AlertCircle, CheckCircle2, User, Scan, Gift, Banknote, ExternalLink, Loader2,
  QrCode, Copy, Clock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import CameraBarcodeScanner from '../components/CameraBarcodeScanner';
import { productsAPI, ordersAPI, shoppingSessionAPI, stripeAPI } from '../services/api';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const TAX_RATE = 0.13;

const InStoreShopping = ({ user, onNavigateToLogin }) => {
  const [cart, setCart] = useState([]);
  const [products, setProducts] = useState([]);
  const [showScanner, setShowScanner] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [orderComplete, setOrderComplete] = useState(null);
  const [checkoutMode, setCheckoutMode] = useState(null); // 'self' or 'cashier'
  const [sessionId, setSessionId] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('card'); // 'card', 'gift', 'cash'
  const [giftCardCode, setGiftCardCode] = useState('');
  const [giftCardApplied, setGiftCardApplied] = useState(null);
  const [stripeEnabled, setStripeEnabled] = useState(false);
  // Transfer to cashier state
  const [transferData, setTransferData] = useState(null);
  const [showTransferModal, setShowTransferModal] = useState(false);
  // Guest checkout state
  const [showGuestCheckout, setShowGuestCheckout] = useState(false);
  const [guestEmail, setGuestEmail] = useState('');
  const [guestName, setGuestName] = useState('');
  const [guestPhone, setGuestPhone] = useState('');

  // Fetch products on mount
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const data = await productsAPI.getAll();
        setProducts(data.map(p => ({ ...p, inStock: p.in_stock })));
      } catch (error) {
        console.error('Error fetching products:', error);
      }
    };
    fetchProducts();
  }, []);

  // Check if Stripe is enabled
  useEffect(() => {
    const checkStripe = async () => {
      try {
        const result = await stripeAPI.getPublicKey();
        setStripeEnabled(result.enabled && !!result.publishable_key);
      } catch (error) {
        console.error('Error checking Stripe:', error);
        setStripeEnabled(false);
      }
    };
    checkStripe();
  }, []);

  // Create/update shopping session when user logs in or cart changes
  useEffect(() => {
    if (user && cart.length > 0) {
      updateShoppingSession();
    }
  }, [user, cart]);

  // Cleanup session on unmount or when cart is cleared
  useEffect(() => {
    return () => {
      if (sessionId && user) {
        endShoppingSession();
      }
    };
  }, [sessionId, user]);

  const updateShoppingSession = async () => {
    if (!user) return;
    
    // Calculate cart total directly to avoid timing issues with useMemo
    const subtotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
    const taxableSubtotal = cart
      .filter(item => item.taxable)
      .reduce((sum, item) => sum + item.price * item.quantity, 0);
    const tax = taxableSubtotal * TAX_RATE;
    const cartTotal = subtotal + tax;
    
    try {
      const sessionData = {
        user_id: user.id,
        user_name: user.name,
        user_email: user.email,
        type: 'in-store',
        cart_items: cart.map(item => ({
          product_id: item.id,
          name: item.name,
          price: item.price,
          quantity: item.quantity
        })),
        cart_total: cartTotal,
        status: 'active'
      };
      
      const response = await shoppingSessionAPI.upsert(sessionData, sessionId);
      if (response && response.id) {
        setSessionId(response.id);
      }
    } catch (error) {
      console.error('Error updating shopping session:', error);
    }
  };

  const endShoppingSession = async () => {
    if (!sessionId) return;
    try {
      await shoppingSessionAPI.end(sessionId);
      setSessionId(null);
    } catch (error) {
      console.error('Error ending session:', error);
    }
  };

  // Calculate totals
  const calculations = useMemo(() => {
    const subtotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
    const taxableSubtotal = cart
      .filter(item => item.taxable)
      .reduce((sum, item) => sum + item.price * item.quantity, 0);
    const tax = taxableSubtotal * TAX_RATE;
    const total = subtotal + tax;
    return { subtotal, tax, total, itemCount: cart.reduce((sum, item) => sum + item.quantity, 0) };
  }, [cart]);

  // Handle barcode scan
  const handleBarcodeScan = (barcode) => {
    const product = products.find(p => p.barcode === barcode);
    if (product) {
      addToCart(product);
      toast.success(`Added: ${product.name}`);
    } else {
      toast.error(`Product not found for barcode: ${barcode}`);
    }
  };

  // Search products
  const searchResults = useMemo(() => {
    if (!searchQuery || searchQuery.length < 2) return [];
    const query = searchQuery.toLowerCase();
    return products.filter(p => 
      p.name.toLowerCase().includes(query) || 
      (p.barcode && p.barcode.includes(query))
    ).slice(0, 5);
  }, [searchQuery, products]);

  // Cart functions
  const addToCart = (product) => {
    setCart(prev => {
      const existing = prev.find(item => item.id === product.id);
      if (existing) {
        return prev.map(item =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, { ...product, quantity: 1 }];
    });
  };

  const updateQuantity = (productId, delta) => {
    setCart(prev => {
      return prev.map(item => {
        if (item.id === productId) {
          const newQty = item.quantity + delta;
          return newQty > 0 ? { ...item, quantity: newQty } : null;
        }
        return item;
      }).filter(Boolean);
    });
  };

  const removeFromCart = (productId) => {
    setCart(prev => prev.filter(item => item.id !== productId));
  };

  const clearCart = () => {
    setCart([]);
    setShowCheckout(false);
    setCheckoutMode(null);
  };

  // Handle checkout
  const handleCheckout = async (mode) => {
    if (!user) {
      // Show guest checkout option instead of requiring login
      setCheckoutMode(mode);
      setShowGuestCheckout(true);
      return;
    }

    setCheckoutMode(mode);
    
    if (mode === 'cashier') {
      // Create a transfer to the cashier
      setIsProcessing(true);
      try {
        const token = localStorage.getItem('auth_token');
        
        const transferRequest = {
          items: cart.map(item => ({
            product_id: item.id,
            name: item.name,
            price: item.price,
            quantity: item.quantity,
            taxable: item.taxable || false,
            image: item.image || null
          })),
          subtotal: calculations.subtotal,
          tax: calculations.tax,
          total: calculations.total,
          customer_name: user.name,
          customer_email: user.email,
          customer_phone: user.phone || null
        };

        const response = await fetch(`${API_URL}/api/pos/transfer/create`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(transferRequest)
        });

        if (response.ok) {
          const data = await response.json();
          setTransferData(data);
          setShowTransferModal(true);
          await endShoppingSession();
          toast.success('Transfer created! Show the code to the cashier.');
        } else {
          const error = await response.json();
          toast.error(error.detail || 'Failed to create transfer');
        }
      } catch (error) {
        console.error('Transfer error:', error);
        toast.error('Failed to create transfer. Please try again.');
      } finally {
        setIsProcessing(false);
      }
    } else {
      // Self checkout - show payment options
      setShowCheckout(true);
    }
  };

  // Handle guest checkout
  const handleGuestCheckout = async () => {
    if (!guestEmail) {
      toast.error('Please enter your email');
      return;
    }
    
    if (checkoutMode === 'cashier') {
      // Guest transfer to cashier
      setIsProcessing(true);
      try {
        const transferRequest = {
          items: cart.map(item => ({
            product_id: item.id,
            name: item.name,
            price: item.price,
            quantity: item.quantity,
            taxable: item.taxable || false,
            image: item.image || null
          })),
          subtotal: calculations.subtotal,
          tax: calculations.tax,
          total: calculations.total,
          customer_name: guestName || 'Guest',
          customer_email: guestEmail,
          customer_phone: guestPhone || null,
          is_guest: true
        };

        const response = await fetch(`${API_URL}/api/pos/transfer/create-guest`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(transferRequest)
        });

        if (response.ok) {
          const data = await response.json();
          setTransferData(data);
          setShowTransferModal(true);
          setShowGuestCheckout(false);
          toast.success('Transfer created! Show the code to the cashier.');
        } else {
          const error = await response.json();
          toast.error(error.detail || 'Failed to create transfer');
        }
      } catch (error) {
        console.error('Transfer error:', error);
        toast.error('Failed to create transfer. Please try again.');
      } finally {
        setIsProcessing(false);
      }
    } else {
      // Guest self checkout
      setShowGuestCheckout(false);
      setShowCheckout(true);
    }
  };

  // Handle guest self-checkout payment
  const handleGuestSelfCheckoutPayment = async () => {
    if (!guestEmail) {
      toast.error('Please enter your email');
      return;
    }

    setIsProcessing(true);
    try {
      const orderData = {
        items: cart.map(item => ({
          product_id: item.id,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          taxable: item.taxable || false,
        })),
        delivery_info: {
          name: guestName || 'Guest',
          email: guestEmail,
          phone: guestPhone || '',
          address: 'In-Store Self-Checkout',
          city: 'Ottawa',
          province: 'ON',
          postal_code: 'K2A 1C4',
          instructions: 'Guest self-checkout order',
        },
        payment_method: paymentMethod,
        subtotal: calculations.subtotal,
        tax: calculations.tax,
        delivery_fee: 0,
        total: finalTotal,
        gift_card_code: giftCardApplied?.code || null,
        gift_card_discount: giftCardApplied?.amount || 0,
        is_guest: true,
      };

      // Create guest order
      const response = await fetch(`${API_URL}/api/orders/guest`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(orderData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create order');
      }

      const order = await response.json();

      // Handle Stripe payment
      if (paymentMethod === 'card' && stripeEnabled) {
        const originUrl = window.location.origin;
        const checkoutResult = await stripeAPI.createCheckout(order.id, originUrl);
        window.location.href = checkoutResult.checkout_url;
        return;
      }

      // For cash or gift card payments, complete immediately
      setOrderComplete({ 
        id: order.id, 
        mode: 'self',
        total: finalTotal,
        paymentMethod: paymentMethod,
      });
      setCart([]);
      setShowCheckout(false);
      setGiftCardApplied(null);
      setGiftCardCode('');
      setGuestEmail('');
      setGuestName('');
      setGuestPhone('');
      toast.success('Payment successful!');
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.message || 'Payment failed. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Copy transfer code to clipboard
  const copyTransferCode = () => {
    if (transferData?.transfer_code) {
      navigator.clipboard.writeText(transferData.transfer_code);
      toast.success('Code copied to clipboard!');
    }
  };

  // Close transfer modal and reset
  const closeTransferModal = () => {
    setShowTransferModal(false);
    setTransferData(null);
    setCart([]);
    setCheckoutMode(null);
  };

  // Apply gift card
  const applyGiftCard = () => {
    if (giftCardCode.toLowerCase() === 'gift50') {
      setGiftCardApplied({ code: giftCardCode, amount: 50 });
      toast.success('Gift card applied! $50.00 discount');
    } else if (giftCardCode.toLowerCase() === 'gift25') {
      setGiftCardApplied({ code: giftCardCode, amount: 25 });
      toast.success('Gift card applied! $25.00 discount');
    } else {
      toast.error('Invalid gift card code');
    }
  };

  // Calculate final total with gift card
  const finalTotal = Math.max(0, calculations.total - (giftCardApplied?.amount || 0));

  // Handle self-checkout payment
  const handleSelfCheckoutPayment = async () => {
    // If guest checkout, use guest payment handler
    if (!user) {
      return handleGuestSelfCheckoutPayment();
    }

    setIsProcessing(true);
    try {
      const orderData = {
        items: cart.map(item => ({
          product_id: item.id,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          taxable: item.taxable || false,
        })),
        delivery_info: {
          name: user.name,
          email: user.email,
          phone: user.phone || '',
          address: 'In-Store Self-Checkout',
          city: 'Ottawa',
          province: 'ON',
          postal_code: 'K2A 1C4',
          instructions: 'Self-checkout order',
        },
        payment_method: paymentMethod,
        subtotal: calculations.subtotal,
        tax: calculations.tax,
        delivery_fee: 0,
        total: finalTotal,
        gift_card_code: giftCardApplied?.code || null,
        gift_card_discount: giftCardApplied?.amount || 0,
      };

      const order = await ordersAPI.create(orderData);

      // Handle Stripe payment
      if (paymentMethod === 'card' && stripeEnabled) {
        const originUrl = window.location.origin;
        const checkoutResult = await stripeAPI.createCheckout(order.id, originUrl);
        window.location.href = checkoutResult.checkout_url;
        return;
      }

      // For cash or gift card payments, complete immediately
      await endShoppingSession();
      setOrderComplete({ 
        id: order.id, 
        mode: 'self',
        total: finalTotal,
        paymentMethod: paymentMethod,
      });
      setCart([]);
      setShowCheckout(false);
      setGiftCardApplied(null);
      setGiftCardCode('');
      toast.success('Payment successful!');
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.message || 'Payment failed. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  // If user not logged in, show guest mode notice (but allow shopping)
  // Cart functionality works for guests, but checkout requires sign-in

  // Order complete screen
  if (orderComplete) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4" data-testid="order-complete-screen">
        <Card className="max-w-md w-full p-8 text-center">
          <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            {orderComplete.mode === 'cashier' ? 'Order Sent to Cashier!' : 'Payment Successful!'}
          </h2>
          <p className="text-gray-500 mb-4">
            {orderComplete.mode === 'cashier' 
              ? 'Please proceed to the cashier to complete your payment.'
              : 'Your purchase has been completed. Thank you for shopping with us!'}
          </p>
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <p className="text-sm text-gray-500">Order ID</p>
            <p className="font-mono text-lg font-bold text-gray-800">{orderComplete.id.slice(0, 8).toUpperCase()}</p>
            <p className="text-2xl font-bold text-orange-600 mt-2">${orderComplete.total.toFixed(2)}</p>
          </div>
          <Button 
            onClick={() => setOrderComplete(null)}
            className="w-full bg-orange-500 hover:bg-orange-600 text-white"
            data-testid="new-shopping-btn"
          >
            Start New Shopping
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-6" data-testid="in-store-shopping-page">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-1">In-Store Shopping</h1>
            <p className="text-gray-500 text-sm md:text-base">Scan items to add to your cart, then checkout instantly</p>
          </div>
          {!user && (
            <Button 
              onClick={onNavigateToLogin}
              className="bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="guest-sign-in-btn"
            >
              <User className="w-4 h-4 mr-2" />
              Sign In
            </Button>
          )}
        </div>
        
        {/* Guest mode banner */}
        {!user && (
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm text-amber-800">
                <span className="font-medium">Shopping as Guest</span> — You can scan and add items to your cart. Sign in when ready to checkout.
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Area - Scanner & Search */}
        <div className="lg:col-span-2 space-y-4">
          {/* Scan Button */}
          <Card className="p-6 bg-gradient-to-br from-orange-500 to-orange-600 text-white">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold mb-1">Scan Products</h2>
                <p className="text-orange-100 text-sm">Use the scanner app to scan barcodes</p>
              </div>
              <Button 
                onClick={() => setShowScanner(true)}
                className="bg-white text-orange-600 hover:bg-orange-50 h-14 px-6"
                data-testid="open-scanner-btn"
              >
                <Scan className="w-5 h-5 mr-2" />
                Scan Barcode
              </Button>
            </div>
          </Card>

          {/* Search */}
          <Card className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Search by product name or barcode..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="product-search-input"
              />
            </div>
            
            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="mt-3 border-t pt-3 space-y-2" data-testid="search-results">
                {searchResults.map(product => (
                  <div 
                    key={product.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <img 
                        src={product.image} 
                        alt={product.name}
                        className="w-12 h-12 rounded-lg object-cover"
                      />
                      <div>
                        <p className="font-medium text-gray-800 text-sm">{product.name}</p>
                        <p className="text-orange-600 font-bold">${product.price.toFixed(2)}</p>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => {
                        addToCart(product);
                        setSearchQuery('');
                        toast.success(`Added: ${product.name}`);
                      }}
                      className="bg-orange-500 hover:bg-orange-600 text-white"
                      data-testid={`add-product-${product.id}`}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Cart Items (Large Screen) */}
          <Card className="hidden lg:block p-4">
            <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <ShoppingCart className="w-5 h-5" />
              Cart Items ({calculations.itemCount})
            </h3>
            
            {cart.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <ScanLine className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>Scan items to add to cart</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {cart.map(item => (
                  <div 
                    key={item.id}
                    className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
                    data-testid={`cart-item-${item.id}`}
                  >
                    <img 
                      src={item.image} 
                      alt={item.name}
                      className="w-14 h-14 rounded-lg object-cover"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-800 text-sm truncate">{item.name}</p>
                      <p className="text-orange-600 font-bold">${item.price.toFixed(2)}</p>
                      {item.taxable && (
                        <Badge variant="outline" className="text-xs">+Tax</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="icon"
                        variant="outline"
                        className="h-8 w-8"
                        onClick={() => updateQuantity(item.id, -1)}
                        data-testid={`decrease-qty-${item.id}`}
                      >
                        <Minus className="w-3 h-3" />
                      </Button>
                      <span className="w-8 text-center font-medium">{item.quantity}</span>
                      <Button
                        size="icon"
                        variant="outline"
                        className="h-8 w-8"
                        onClick={() => updateQuantity(item.id, 1)}
                        data-testid={`increase-qty-${item.id}`}
                      >
                        <Plus className="w-3 h-3" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
                        onClick={() => removeFromCart(item.id)}
                        data-testid={`remove-item-${item.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Sidebar - Summary & Checkout */}
        <div className="space-y-4">
          <Card className="p-4 sticky top-4">
            <h3 className="font-semibold text-gray-800 mb-4">Order Summary</h3>
            
            {/* Mobile Cart Preview */}
            <div className="lg:hidden mb-4">
              {cart.length > 0 && (
                <div className="space-y-2 max-h-48 overflow-y-auto border-b pb-4 mb-4">
                  {cart.map(item => (
                    <div key={item.id} className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">{item.quantity}x {item.name}</span>
                      <span className="font-medium">${(item.price * item.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2 mb-4">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Items ({calculations.itemCount})</span>
                <span className="text-gray-800">${calculations.subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">HST (13%)</span>
                <span className="text-gray-800">${calculations.tax.toFixed(2)}</span>
              </div>
              <div className="border-t pt-2 flex justify-between">
                <span className="font-semibold text-gray-800">Total</span>
                <span className="font-bold text-xl text-orange-600">${calculations.total.toFixed(2)}</span>
              </div>
            </div>

            {cart.length > 0 && !showCheckout && (
              <div className="space-y-3">
                <Button 
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white h-12"
                  onClick={() => handleCheckout('self')}
                  disabled={isProcessing}
                  data-testid="self-checkout-btn"
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  Self-Checkout
                </Button>
                <Button 
                  variant="outline"
                  className="w-full h-12"
                  onClick={() => handleCheckout('cashier')}
                  disabled={isProcessing}
                  data-testid="send-to-cashier-btn"
                >
                  <Receipt className="w-4 h-4 mr-2" />
                  Send to Cashier
                </Button>
                <Button 
                  variant="ghost"
                  className="w-full text-gray-500"
                  onClick={clearCart}
                  data-testid="clear-cart-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Clear Cart
                </Button>
              </div>
            )}

            {/* Self-Checkout Payment */}
            {showCheckout && (
              <div className="space-y-4" data-testid="self-checkout-form">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-gray-800">Choose Payment Method</h4>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => {
                      setShowCheckout(false);
                      setGiftCardApplied(null);
                      setGiftCardCode('');
                    }}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>

                <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod} className="space-y-3">
                  {/* Card Payment */}
                  <div className={`border rounded-xl p-4 cursor-pointer transition-all ${
                    paymentMethod === 'card' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                  }`}>
                    <div className="flex items-center gap-3">
                      <RadioGroupItem value="card" id="card" />
                      <Label htmlFor="card" className="flex items-center gap-2 cursor-pointer flex-1">
                        <CreditCard className="w-5 h-5 text-blue-500" />
                        <span className="font-medium">Credit/Debit Card</span>
                      </Label>
                    </div>
                    
                    {paymentMethod === 'card' && (
                      <div className="mt-3 pl-7">
                        {stripeEnabled ? (
                          <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-3 border border-blue-100">
                            <div className="flex items-center gap-2 text-sm">
                              <ExternalLink className="w-4 h-4 text-blue-600" />
                              <span className="text-blue-700">Secure payment via Stripe</span>
                            </div>
                          </div>
                        ) : (
                          <div className="bg-yellow-50 rounded-lg p-3 border border-yellow-200">
                            <div className="flex items-center gap-2 text-sm text-yellow-700">
                              <AlertCircle className="w-4 h-4" />
                              <span>Card payments not configured</span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Gift Card */}
                  <div className={`border rounded-xl p-4 cursor-pointer transition-all ${
                    paymentMethod === 'gift' ? 'border-purple-500 bg-purple-50' : 'border-gray-200'
                  }`}>
                    <div className="flex items-center gap-3">
                      <RadioGroupItem value="gift" id="gift" />
                      <Label htmlFor="gift" className="flex items-center gap-2 cursor-pointer flex-1">
                        <Gift className="w-5 h-5 text-purple-500" />
                        <span className="font-medium">Gift Card</span>
                      </Label>
                    </div>
                    
                    {paymentMethod === 'gift' && (
                      <div className="mt-3 pl-7 space-y-2">
                        <div className="flex gap-2">
                          <Input 
                            value={giftCardCode}
                            onChange={(e) => setGiftCardCode(e.target.value)}
                            placeholder="Enter gift card code"
                            className="flex-1"
                          />
                          <Button onClick={applyGiftCard} variant="outline" size="sm">
                            Apply
                          </Button>
                        </div>
                        {giftCardApplied && (
                          <p className="text-green-600 text-sm flex items-center gap-1">
                            <Check className="w-4 h-4" />
                            Applied: -${giftCardApplied.amount.toFixed(2)}
                          </p>
                        )}
                        <p className="text-xs text-gray-500">Test codes: GIFT25, GIFT50</p>
                      </div>
                    )}
                  </div>

                  {/* Cash */}
                  <div className={`border rounded-xl p-4 cursor-pointer transition-all ${
                    paymentMethod === 'cash' ? 'border-green-500 bg-green-50' : 'border-gray-200'
                  }`}>
                    <div className="flex items-center gap-3">
                      <RadioGroupItem value="cash" id="cash" />
                      <Label htmlFor="cash" className="flex items-center gap-2 cursor-pointer flex-1">
                        <Banknote className="w-5 h-5 text-green-500" />
                        <span className="font-medium">Cash</span>
                      </Label>
                    </div>
                    
                    {paymentMethod === 'cash' && (
                      <div className="mt-3 pl-7">
                        <p className="text-sm text-gray-600">
                          Pay with cash at the self-checkout kiosk
                        </p>
                      </div>
                    )}
                  </div>
                </RadioGroup>

                {/* Total with discount */}
                {giftCardApplied && (
                  <div className="border-t pt-3 space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Subtotal</span>
                      <span>${calculations.total.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm text-green-600">
                      <span>Gift Card ({giftCardApplied.code})</span>
                      <span>-${giftCardApplied.amount.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between font-bold text-lg">
                      <span>Total</span>
                      <span className="text-orange-600">${finalTotal.toFixed(2)}</span>
                    </div>
                  </div>
                )}

                <Button 
                  className="w-full bg-green-600 hover:bg-green-700 text-white h-12"
                  onClick={handleSelfCheckoutPayment}
                  disabled={isProcessing || (paymentMethod === 'card' && !stripeEnabled) || (paymentMethod === 'gift' && !giftCardApplied)}
                  data-testid="complete-payment-btn"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : paymentMethod === 'card' && stripeEnabled ? (
                    <>
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Pay ${finalTotal.toFixed(2)} with Stripe
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Pay ${finalTotal.toFixed(2)}
                    </>
                  )}
                </Button>
              </div>
            )}

            {cart.length === 0 && (
              <div className="text-center py-4 text-gray-400">
                <p className="text-sm">Your cart is empty</p>
              </div>
            )}
          </Card>

          {/* Store Info */}
          <Card className="p-4 bg-gray-50">
            <div className="flex items-center gap-3 mb-3">
              <Store className="w-5 h-5 text-gray-600" />
              <span className="font-medium text-gray-800">UPLANDS FOOD MARKET</span>
            </div>
            <p className="text-sm text-gray-500">
              1678 Carling Avenue, Ottawa, ON K2A 1C4
            </p>
            <p className="text-sm text-green-600 mt-2 font-medium">✓ Open Now</p>
          </Card>
        </div>
      </div>

      {/* Camera Barcode Scanner - Like Walmart/Easy Buy */}
      <CameraBarcodeScanner
        isOpen={showScanner}
        onClose={() => setShowScanner(false)}
        onScan={(barcode, product) => {
          if (product) {
            handleBarcodeScan(barcode);
          } else {
            toast.error(`Product with barcode ${barcode} not found`);
          }
        }}
        products={products}
        onAddToCart={(product) => {
          addToCart(product);
        }}
      />

      {/* Transfer to Cashier Modal */}
      {showTransferModal && transferData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="transfer-modal">
          <Card className="w-full max-w-md p-6 animate-in fade-in zoom-in duration-200">
            <div className="text-center">
              {/* Header */}
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <Receipt className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Transfer Ready!</h2>
              <p className="text-gray-500 mb-6">Show this code to the cashier to complete your purchase</p>
              
              {/* Transfer Code */}
              <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-6 mb-6">
                <p className="text-sm text-orange-600 font-medium mb-2">TRANSFER CODE</p>
                <div className="flex items-center justify-center gap-3">
                  <span className="text-4xl font-mono font-bold tracking-[0.3em] text-gray-800">
                    {transferData.transfer_code}
                  </span>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8"
                    onClick={() => {
                      navigator.clipboard.writeText(transferData.transfer_code);
                      toast.success('Code copied!');
                    }}
                    data-testid="copy-transfer-code"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* QR Code if available */}
              {transferData.qr_code && (
                <div className="mb-6">
                  <img 
                    src={transferData.qr_code} 
                    alt="Transfer QR Code" 
                    className="w-32 h-32 mx-auto"
                  />
                </div>
              )}

              {/* Order Summary */}
              <div className="text-left bg-gray-50 rounded-lg p-4 mb-6">
                <p className="text-sm font-medium text-gray-700 mb-2">Order Summary</p>
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Items</span>
                  <span>{transferData.items?.length || 0}</span>
                </div>
                <div className="flex justify-between text-sm font-medium text-gray-800 mt-1">
                  <span>Total</span>
                  <span className="text-orange-600">${transferData.total.toFixed(2)}</span>
                </div>
              </div>

              {/* Expiration Notice */}
              <div className="flex items-center justify-center gap-2 text-sm text-amber-600 mb-6">
                <Clock className="w-4 h-4" />
                <span>Expires in {transferData.expires_in_minutes} minutes</span>
              </div>

              {/* Actions */}
              <div className="space-y-3">
                <Button 
                  className="w-full bg-green-600 hover:bg-green-700 text-white"
                  onClick={closeTransferModal}
                  data-testid="done-transfer-btn"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Done
                </Button>
                <p className="text-xs text-gray-400">
                  The cashier will enter this code at the POS to retrieve your cart
                </p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Guest Checkout Modal */}
      {showGuestCheckout && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="guest-checkout-modal">
          <Card className="w-full max-w-md p-6 animate-in fade-in zoom-in duration-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-800">Guest Checkout</h2>
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => setShowGuestCheckout(false)}
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <p className="text-gray-500 text-sm mb-6">
              Enter your details to complete your purchase. No account required!
            </p>

            <div className="space-y-4">
              <div>
                <Label htmlFor="guest-email">Email <span className="text-red-500">*</span></Label>
                <Input
                  id="guest-email"
                  type="email"
                  placeholder="your@email.com"
                  value={guestEmail}
                  onChange={(e) => setGuestEmail(e.target.value)}
                  className="mt-1"
                  data-testid="guest-email-input"
                />
                <p className="text-xs text-gray-500 mt-1">We'll send your receipt here</p>
              </div>

              <div>
                <Label htmlFor="guest-name">Name (Optional)</Label>
                <Input
                  id="guest-name"
                  type="text"
                  placeholder="Your name"
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                  className="mt-1"
                  data-testid="guest-name-input"
                />
              </div>

              <div>
                <Label htmlFor="guest-phone">Phone (Optional)</Label>
                <Input
                  id="guest-phone"
                  type="tel"
                  placeholder="+1 (613) 555-0123"
                  value={guestPhone}
                  onChange={(e) => setGuestPhone(e.target.value)}
                  className="mt-1"
                  data-testid="guest-phone-input"
                />
              </div>

              <div className="border-t pt-4 mt-4">
                <div className="flex justify-between text-sm text-gray-600 mb-2">
                  <span>Total</span>
                  <span className="font-bold text-lg text-orange-600">${calculations.total.toFixed(2)}</span>
                </div>
              </div>

              <div className="space-y-3">
                <Button 
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white h-12"
                  onClick={handleGuestCheckout}
                  disabled={isProcessing || !guestEmail}
                  data-testid="continue-guest-checkout-btn"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : checkoutMode === 'cashier' ? (
                    <>
                      <Receipt className="w-4 h-4 mr-2" />
                      Get Cashier Code
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-4 h-4 mr-2" />
                      Continue to Payment
                    </>
                  )}
                </Button>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200"></div>
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span className="px-2 bg-white text-gray-400">or</span>
                  </div>
                </div>

                <Button 
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    setShowGuestCheckout(false);
                    onNavigateToLogin();
                  }}
                  data-testid="sign-in-instead-btn"
                >
                  <User className="w-4 h-4 mr-2" />
                  Sign In Instead
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default InStoreShopping;
